import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fieldClass =
  'w-full bg-[#0c0f17]/60 border border-[#262A36] rounded-lg px-4 py-3 text-sm text-white placeholder-[#4b5563] focus:border-[#1E8F89] focus:outline-none focus:ring-1 focus:ring-[#1E8F89]/40 transition-colors';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error('Passphrases do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true });
      setDone(true);
      toast.success('Passphrase reset');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Unable to reset passphrase');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#08080A] px-6 scanlines" data-testid="reset-password-page">
      <div className="w-full max-w-md glass-hud border border-[#262A36] rounded-xl p-8 relative z-10">
        <p className="mono-label mb-2">Archive Access Token</p>
        <h1 className="font-serif-display text-3xl font-light tracking-tight text-white mb-6">
          {done ? 'Passphrase Reset' : 'Set New Passphrase'}
        </h1>

        {!token ? (
          <div className="space-y-5" data-testid="reset-missing-token">
            <p className="text-sm text-[#949BAA] font-mono-tech leading-relaxed">
              No access token found in this link. Request a new reset transmission from the access panel.
            </p>
            <Link to="/" className="btn-ambient w-full inline-block text-center" data-testid="reset-home-link">
              Return to the Archive
            </Link>
          </div>
        ) : done ? (
          <div className="space-y-5" data-testid="reset-success">
            <p className="text-sm text-[#949BAA] font-mono-tech leading-relaxed">
              Your passphrase has been updated and existing sessions cleared. You may now enter the archive.
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="btn-primary w-full"
              data-testid="reset-enter-button"
            >
              Enter the Archive
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
            <p className="text-sm text-[#6B7280] font-mono-tech leading-relaxed mb-2">
              This single-use token expires 30 minutes after issue.
            </p>
            <div>
              <label className="mono-meta mb-2 block">New passphrase</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className={fieldClass}
                data-testid="reset-password-input"
              />
            </div>
            <div>
              <label className="mono-meta mb-2 block">Confirm passphrase</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                className={fieldClass}
                data-testid="reset-confirm-input"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit-button">
              {loading ? 'Resetting…' : 'Reset passphrase'}
            </button>
            <Link
              to="/"
              className="block w-full text-center font-mono-tech text-[0.62rem] uppercase tracking-[0.2em] text-[#6B7280] hover:text-[#26B0A8] transition-colors pt-1"
              data-testid="reset-cancel-link"
            >
              ← Back to the Archive
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
